import { createId } from '@paralleldrive/cuid2'
import {
	createAsyncThunk,
	createSlice,
	type PayloadAction,
} from '@reduxjs/toolkit'
import i18next from 'i18next'
import { cloneDeep, isEqual, orderBy } from 'lodash'
import * as customMutations from 'src/graphql/customMutations'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { projectApi } from 'src/redux/api/graphql/projectsApi'
import { fetchContacts } from 'src/redux/slices/contacts'
import { fetchAllQuotes } from 'src/redux/slices/quotes'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type {
	ReduxCompany,
	ReduxContact as ReduxContacts,
	ReduxInvoices,
	ReduxProjects,
	ReduxProposals,
	ReduxQuote,
} from 'src/types/apiType'
import type { ApiStatus } from 'src/types/miscType'
import type { QuoteTotals } from 'src/types/quoteTotalsTypes'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import {
	fetchProposalsByTenantIdUtil,
	fetchQuoteInfoUtil,
	fetchRecapInfoUtil,
} from 'src/utils/fetchUtils'
import {
	calculateProposalAndInvoiceTotals,
	createUpdateData,
} from 'src/utils/invoiceUtils/proposalUtils'
import {
	makeErrorToast,
	makeSuccessToast,
	makeWarningToast,
	msg,
} from 'src/utils/makeToast'
import getQuoteTotalSummary from 'src/utils/quote/quoteTotalsSummary/getQuoteTotalSummary'
import {
	getProperUpdateValues,
	hasBusinessChanges,
} from 'src/utils/updateApiUtil'
import { quoteInfoApi } from '@/redux/api/graphql/quoteInfoApi'
import { recapInfoApi } from '@/redux/api/graphql/recapInfoApi'
import { getErrorMessage } from '@/utils/error'

type AsyncThunkConfig = {
	state: RootState
	dispatch: ThunkAppDispatch
	rejectValue: string
}

type QuoteSummary = Pick<
	ReduxQuote,
	'id' | '_version' | 'name' | 'quoteType' | 'quoteNumber' | 'parentProjectId'
> &
	Partial<ReduxQuote>

export type ProposalFormValues = {
	contact: string
	drafts: {
		poNumber: string
		proposalAmountPercentage: string
		amount: string
		invoiceDate: string
		invoiceDueDate: string
		description: string
		readyStatus: boolean
	}[]
	invoiceCount?: string
}

type ProposalPoDetails = Partial<ReduxProposals> & {
	poAmount?: string | number | null
}

type RemoveProposalArgs = {
	quoteData: ReduxQuote
	isQuoteList?: boolean
}

type ToggleExpiryArgs = {
	proposalId: string
}

type CreateProposalArgs = {
	quoteData: QuoteSummary
	newQuote: Partial<ReduxQuote>
	isQuoteList?: boolean
}

type AddPoArgs = {
	currentProposal: ReduxProposals
	poDetails: ProposalPoDetails
	poBucket?: unknown
}

type UpdateProposalArgs = {
	newProposal: ProposalFormValues
	currentProposal: ReduxProposals
	quoteTotals?: QuoteTotals | null
}

type UpdateProposalProgressArgs = {
	newProposal: ProposalFormValues
	currentProposal: ReduxProposals
}

type UpdateProposalFromPreviewArgs = {
	newProposal: ProposalFormValues
	currentProposal: ReduxProposals | null
	quoteTotals?: QuoteTotals | null
}

type UpdateProposalAmountArgs = {
	currentProposal: ReduxProposals
}

type ProposalsState = {
	proposalsData: ReduxProposals[]
	fetchProposalsApiStatus: ApiStatus
	createProposalApiStatus: ApiStatus
	removeProposalApiStatus: ApiStatus
	updateProposalApiStatus: ApiStatus
	updateProposalFromPreviewApiStatus: ApiStatus
	updateProposalAmountApiStatus: ApiStatus
	addPOToProposalApiStatus: ApiStatus
	editPOInProposalApiStatus: ApiStatus
	toggleExpiryApiStatus: ApiStatus
	currentDraftIndex: number | null
	currentDraftInvoiceId: string | null
	proposalModalOpenStatus: boolean
	proposalsPOModalOpenStatus: boolean
	editProposalsPOModalOpenStatus: boolean
	pdfModalStatusOpen: boolean
	currentProposal: ReduxProposals | null
	currentProposalInvoices: ReduxInvoices[] | null
	error: string | null
}

const initialState: ProposalsState = {
	proposalsData: [],
	fetchProposalsApiStatus: 'idle',
	createProposalApiStatus: 'idle',
	removeProposalApiStatus: 'idle',
	updateProposalApiStatus: 'idle',
	updateProposalFromPreviewApiStatus: 'idle',
	updateProposalAmountApiStatus: 'idle',
	addPOToProposalApiStatus: 'idle',
	editPOInProposalApiStatus: 'idle',
	toggleExpiryApiStatus: 'idle',
	currentDraftIndex: null,
	currentDraftInvoiceId: null,
	proposalModalOpenStatus: false,
	proposalsPOModalOpenStatus: false,
	editProposalsPOModalOpenStatus: false,
	pdfModalStatusOpen: false,
	currentProposal: null,
	currentProposalInvoices: null,
	error: null,
}

export const fetchProposals = createAsyncThunk<
	ReduxProposals[],
	void,
	AsyncThunkConfig
>('proposals/fetchProposals', async (_, { getState, rejectWithValue }) => {
	const { auth } = getState()
	const tenantId = auth.userTableInfo?.currentCompanyTenantId
	if (!tenantId) {
		return rejectWithValue('Tenant ID is not set')
	}
	try {
		const proposalsData = await fetchProposalsByTenantIdUtil(tenantId)
		return proposalsData as ReduxProposals[]
	} catch (err: any) {
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})

export const createProposal = createAsyncThunk<
	void,
	CreateProposalArgs,
	AsyncThunkConfig
>(
	'proposals/createProposal',
	async (
		{ quoteData, newQuote, isQuoteList = false },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			console.log('newQuote: ', newQuote)
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getQuote: originalQuote2 },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteData.id })
			)) as {
				data: { getQuote: ReduxQuote }
			}
			const tProposals = i18next.getFixedT(null, 'proposals')

			const updateInput = {
				id: originalQuote2?.id,
			}
			if (
				originalQuote2?.proposals?.items?.length &&
				originalQuote2?.proposals?.items?.length > 0
			) {
				makeWarningToast(tProposals('toasts.proposalExist'))
				return
			}
			for (const property in newQuote) {
				// @ts-expect-error
				if (!isEqual(newQuote[property], originalQuote2[property])) {
					// @ts-expect-error
					updateInput[property] = newQuote[property]
				}
			}

			console.log('updateInput: ', updateInput)
			const {
				data: { updateQuote: parsedUpdatedQuote },
			} = (await API.graphql(
				graphqlOperation(mutations.updateQuote, { input: updateInput })
			)) as {
				data: { updateQuote: ReduxQuote }
			}
			dispatch(quoteInfoApi.util.invalidateTags([{ type: 'QuoteInfo' }]))
			dispatch(recapInfoApi.util.invalidateTags([{ type: 'RecapInfo' }]))
			const quoteInfo = await fetchQuoteInfoUtil(quoteData.id)
			const recapInfo = await fetchRecapInfoUtil(quoteData.id)

			const {
				data: { getProjects },
			} = (await API.graphql(
				graphqlOperation(queries.getProjects, {
					id: quoteData.parentProjectId,
				})
			)) as {
				data: { getProjects: ReduxProjects }
			}

			const quoteTotals = (await getQuoteTotalSummary({
				quoteDetails: parsedUpdatedQuote,
				quoteInfo,
				recapInfo,
			})) as QuoteTotals
			const newProposalInput: Record<string, unknown> = {
				id: createId(),
				tenantId,
				quoteId: quoteData.id,
				activeFlag: true,
				quoteName: quoteData.name,
				quoteType: quoteData.quoteType,
				quoteNumber: quoteData.quoteNumber,
				projectName: getProjects.name,
				projectNumber: getProjects.projectNumber,
				status: 'Generate without PO',
				total: quoteTotals.discount?.totalAfterDiscount,
			}
			if (parsedUpdatedQuote?.assignedTenantId) {
				newProposalInput.assignedTenantId =
					parsedUpdatedQuote?.assignedTenantId
			}
			if (parsedUpdatedQuote?.assignedTenantName) {
				newProposalInput.assignedTenantName =
					parsedUpdatedQuote?.assignedTenantName
			}

			await API.graphql(
				graphqlOperation(customMutations.createProposalsSimplified, {
					input: newProposalInput,
				})
			)
			// Invalidate projectChildren cache so RTK Query refetches
			await dispatch(
				projectApi.util.invalidateTags([
					{
						type: 'Projects',
						id: `${quoteData.parentProjectId}-children`,
					},
				])
			)
			if (isQuoteList) {
				dispatch(fetchAllQuotes({}))
			}
			dispatch(fetchProposals())
			makeSuccessToast(tProposals('toasts.createdProposal'))
		} catch (err) {
			console.log('err in create proposal: ', err)

			// Check if it's a GraphQL error related to quote relationship
			if (
				(err as any)?.errors?.some((error: any) =>
					error.path?.includes('quote')
				)
			) {
				console.log(
					'Error creating proposal: Quote relationship issue detected'
				)
				makeErrorToast(
					'Error creating proposal: Quote data unavailable. Please refresh and try again.'
				)
			} else if (
				(err as any)?.message?.includes(
					'Cannot return null for non-nullable type'
				)
			) {
				console.log(
					'Error creating proposal: GraphQL nullable field issue'
				)
				makeErrorToast(
					'Error creating proposal: Data validation failed. Please check all required fields.'
				)
			} else {
				makeErrorToast('Error creating proposal. Please try again.')
			}
			const errorMessage = getErrorMessage(err)

			return rejectWithValue(errorMessage)
		}
	}
)
export const addPOToProposal = createAsyncThunk<
	void,
	AddPoArgs,
	AsyncThunkConfig
>(
	'proposals/addPOToProposal',
	async (
		{ currentProposal, poDetails, poBucket },
		{ dispatch, rejectWithValue, getState }
	) => {
		const tProposals = i18next.getFixedT(null, 'proposals')
		try {
			const {
				data: { getProposals },
			} = (await API.graphql(
				graphqlOperation(queries.getProposals, {
					id: currentProposal.id,
				})
			)) as {
				data: { getProposals: ReduxProposals }
			}

			const updateProposal: Record<string, unknown> = {
				...poDetails,
				poVersion: 1,
				status: 'Generate',
			}
			if (poBucket) {
				updateProposal.poDocuments = [poBucket]
			}

			const updateInput = getProperUpdateValues({
				updatedInput: updateProposal,
				originalInput: getProposals,
			}) as Record<string, unknown>
			if (hasBusinessChanges(updateInput)) {
				await API.graphql(
					graphqlOperation(
						customMutations.updateProposalsSimplified,
						{
							input: updateInput,
						}
					)
				)
			}

			dispatch(fetchProposals())

			makeSuccessToast(tProposals('toasts.updatedProposal'))
		} catch (err) {
			makeErrorToast(msg)
			const errorMessage = getErrorMessage(err)
			return rejectWithValue(errorMessage)
		}
	}
)
export const editPOInProposal = createAsyncThunk<
	void,
	AddPoArgs,
	AsyncThunkConfig
>(
	'proposals/editPOInProposal',
	async (
		{ currentProposal, poDetails, poBucket },
		{ dispatch, rejectWithValue }
	) => {
		const tProposals = i18next.getFixedT(null, 'proposals')
		try {
			const {
				data: { getProposals },
			} = (await API.graphql(
				graphqlOperation(queries.getProposals, {
					id: currentProposal.id,
				})
			)) as {
				data: { getProposals: ReduxProposals }
			}

			const updateProposal: Record<string, unknown> = {
				id: getProposals.id,
			}
			if (typeof poDetails.poAmount === 'string') {
				poDetails.poAmount = Number.parseFloat(poDetails.poAmount)
			}
			// Count of keys updated that should update the status
			let count = 0
			const countableKeys = ['poDate', 'poName', 'poAmount', 'poCurrency']
			for (const property in poDetails) {
				// @ts-expect-error
				if (!isEqual(poDetails[property], getProposals[property])) {
					if (countableKeys.includes(property)) {
						count++
					}
					// @ts-expect-error
					updateProposal[property] = poDetails[property]
				}
			}
			if (poBucket) {
				count++
				updateProposal.poDocuments = [poBucket]
			}
			console.log('updateProposal: ', updateProposal)
			if (Object.keys(updateProposal).length > 0) {
				if (getProposals?.status === 'Outdated' && count > 0) {
					updateProposal.status = 'Generate'
				}
				if (count > 0) {
					updateProposal.poVersion = getProposals?.poVersion ?? 1 + 1
				}
				const updateInput = getProperUpdateValues({
					updatedInput: updateProposal,
					originalInput: getProposals,
				}) as Record<string, unknown>
				if (hasBusinessChanges(updateInput)) {
					await API.graphql(
						graphqlOperation(
							customMutations.updateProposalsSimplified,
							{
								input: updateInput,
							}
						)
					)
				}
			}
			dispatch(fetchProposals())
			makeSuccessToast(tProposals('toasts.updatedProposal'))
		} catch (err) {
			makeErrorToast(msg)
			const errorMessage = getErrorMessage(err)
			return rejectWithValue(errorMessage)
		}
	}
)

// Proposal-level bank fields set by `createUpdateData`. Used both to detect a
// bank change in the computed update input and to copy the new values onto the
// proposal's already-generated invoices.
const PROPOSAL_BANK_FIELDS = [
	'bankProfileNumber',
	'proposalsBankProfileName',
	'proposalsBank',
	'proposalsIban',
	'proposalsBic',
	'proposalsAccountName',
] as const

const didBankChange = (updateInput: Record<string, unknown>) =>
	PROPOSAL_BANK_FIELDS.some((field) => field in updateInput)

// Copy the proposal's bank details onto every active invoice already generated
// from it. Each invoice stores its own denormalized copy of the bank fields
// (the invoice PDF reads those copies), so without this a bank change would not
// appear on previously-generated invoices.
const propagateProposalBankToInvoices = async (
	proposal: Pick<
		ReduxProposals,
		| 'id'
		| 'bankProfileNumber'
		| 'proposalsBankProfileName'
		| 'proposalsBank'
		| 'proposalsIban'
		| 'proposalsBic'
		| 'proposalsAccountName'
	>
) => {
	const {
		data: {
			invoicesByProposalId: { items },
		},
	} = (await API.graphql(
		graphqlOperation(queries.invoicesByProposalId, {
			proposalId: proposal.id,
			filter: { activeFlag: { eq: true } },
		})
	)) as {
		data: { invoicesByProposalId: { items: ReduxInvoices[] } }
	}

	await Promise.all(
		items.map((invoice) =>
			API.graphql(
				graphqlOperation(customMutations.updateInvoicesSimplified, {
					input: {
						id: invoice.id,
						bankProfileNumber: proposal.bankProfileNumber,
						invoicesBankProfileName:
							proposal.proposalsBankProfileName,
						invoicesBank: proposal.proposalsBank,
						invoicesIban: proposal.proposalsIban,
						invoicesBic: proposal.proposalsBic,
						invoicesAccountName: proposal.proposalsAccountName,
					},
				})
			)
		)
	)
}

export const updateProposalProgress = createAsyncThunk<
	void,
	UpdateProposalProgressArgs,
	AsyncThunkConfig
>(
	'proposals/updateProposalProgress',
	async (
		{ newProposal, currentProposal },
		{ dispatch, rejectWithValue, getState }
	) => {
		const tProposals = i18next.getFixedT(null, 'proposals')
		const { auth } = getState()
		try {
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: tenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}
			const contactId = newProposal.contact
			if (!contactId) {
				return rejectWithValue('Contact ID is not set')
			}
			const {
				data: { getContact },
			} = (await API.graphql(
				graphqlOperation(queries.getContact, {
					id: contactId,
				})
			)) as {
				data: { getContact: ReduxContacts }
			}
			const {
				data: { getProposals },
			} = (await API.graphql(
				graphqlOperation(queries.getProposals, {
					id: currentProposal.id,
				})
			)) as {
				data: { getProposals: ReduxProposals }
			}
			const originalGetProposals = cloneDeep(getProposals)
			delete getProposals._deleted
			delete getProposals.quote
			delete getProposals.invoices

			const { getProposals: updateProposalData, updateContact } =
				createUpdateData({
					getProposals,
					newProposal,
					getContact,
					getCompany,
				})
			updateProposalData.noofInvoices = newProposal.invoiceCount

			console.log('updateProposalData: ', updateProposalData)
			if (
				updateProposalData.noofInvoices &&
				isNaN(Number.parseInt(updateProposalData.noofInvoices))
			) {
				makeErrorToast(
					'Something Unexpected Happened With Invoice Count!'
				)
			}

			const updateInput = getProperUpdateValues({
				updatedInput: updateProposalData,
				originalInput: originalGetProposals,
			}) as Record<string, unknown>
			if (hasBusinessChanges(updateInput)) {
				await API.graphql(
					graphqlOperation(
						customMutations.updateProposalsSimplified,
						{
							input: updateInput,
						}
					)
				)
			}
			if (Object.keys(updateContact).length > 0) {
				const contactUpdateInput: Record<string, unknown> = {
					...updateContact,
					id: getContact.id,
				}
				await API.graphql(
					graphqlOperation(mutations.updateContact, {
						input: contactUpdateInput,
					})
				)
			}
			if (didBankChange(updateInput)) {
				await propagateProposalBankToInvoices(updateProposalData)
			}
			await dispatch(fetchProposals())
			await dispatch(fetchContacts())
			makeSuccessToast(tProposals('toasts.updatedProposalProgress'))
		} catch (err) {
			makeErrorToast(msg)
			const errMessage = getErrorMessage(err)
			return rejectWithValue(errMessage)
		}
	}
)
export const updateProposal = createAsyncThunk<
	void,
	UpdateProposalArgs,
	AsyncThunkConfig
>(
	'proposals/updateProposal',
	async (
		{ newProposal, currentProposal, quoteTotals },
		{ dispatch, rejectWithValue, getState }
	) => {
		const tProposals = i18next.getFixedT(null, 'proposals')
		const { auth } = getState()
		try {
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: tenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}
			const contactId = newProposal.contact
			if (!contactId) {
				return rejectWithValue('Contact ID is not set')
			}
			const {
				data: { getContact },
			} = (await API.graphql(
				graphqlOperation(queries.getContact, {
					id: contactId,
				})
			)) as {
				data: { getContact: ReduxContacts }
			}
			const {
				data: { getProposals },
			} = (await API.graphql(
				graphqlOperation(queries.getProposals, {
					id: currentProposal.id,
				})
			)) as {
				data: { getProposals: ReduxProposals }
			}
			const originalGetProposals = cloneDeep(getProposals)
			delete getProposals._deleted
			delete getProposals.quote
			delete getProposals.invoices

			console.log('Here2')
			const { getProposals: updateProposalData, updateContact } =
				createUpdateData({
					getProposals,
					newProposal,
					getContact,
					getCompany,
				})
			console.log('getProposals: ', getProposals)
			console.log('updateProposalData: ', updateProposalData)

			console.log('quoteTotals in prop: ', quoteTotals)
			const proposalData = calculateProposalAndInvoiceTotals({
				getProposals: updateProposalData,
				newProposal,
				getContact,
				quoteTotals: quoteTotals ?? null,
				getCompany,
			})
			console.log('proposalData: ', proposalData)
			console.log('proposalData: ', proposalData)

			getProposals.drafts = proposalData.drafts
			getProposals.noofInvoices = proposalData.invoiceCount
			getProposals.paymentTerms = proposalData.paymentTerms
			console.log('getProposals: ', getProposals)
			console.log('updateContact: ', updateContact)
			if (
				getProposals.noofInvoices &&
				isNaN(Number.parseInt(getProposals.noofInvoices))
			) {
				makeErrorToast(
					'Something Unexpected Happened With Invoice Count!'
				)
			}

			const updateInput = getProperUpdateValues({
				updatedInput: getProposals,
				originalInput: originalGetProposals,
			}) as Record<string, unknown>
			if (hasBusinessChanges(updateInput)) {
				await API.graphql(
					graphqlOperation(
						customMutations.updateProposalsSimplified,
						{
							input: updateInput,
						}
					)
				)
			}
			if (Object.keys(updateContact).length > 0) {
				const contactUpdateInput: Record<string, unknown> = {
					...updateContact,
					id: getContact.id,
				}
				await API.graphql(
					graphqlOperation(mutations.updateContact, {
						input: contactUpdateInput,
					})
				)
			}
			if (didBankChange(updateInput)) {
				await propagateProposalBankToInvoices(getProposals)
			}
			await dispatch(fetchProposals())
			await dispatch(fetchContacts())
			makeSuccessToast(tProposals('toasts.updatedProposal'))
		} catch (err) {
			makeErrorToast(msg)
			const errMessage = getErrorMessage(err)
			return rejectWithValue(errMessage)
		}
	}
)

export const updateProposalFromPreview = createAsyncThunk<
	void,
	UpdateProposalFromPreviewArgs,
	AsyncThunkConfig
>(
	'proposals/updateProposalFromPreview',
	async (
		{ newProposal, currentProposal, quoteTotals },
		{ dispatch, rejectWithValue, getState }
	) => {
		console.log('newProposal: ', newProposal)
		const tProposals = i18next.getFixedT(null, 'proposals')
		const { auth } = getState()
		try {
			if (!currentProposal) {
				return rejectWithValue('Current proposal is not set')
			}
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, {
					id: tenantId,
				})
			)) as {
				data: { getCompany: ReduxCompany }
			}
			const contactId = newProposal.contact
			if (!contactId) {
				return rejectWithValue('Contact ID is not set')
			}
			const {
				data: { getContact },
			} = (await API.graphql(
				graphqlOperation(queries.getContact, {
					id: contactId,
				})
			)) as {
				data: { getContact: ReduxContacts }
			}
			const {
				data: { getProposals },
			} = (await API.graphql(
				graphqlOperation(queries.getProposals, {
					id: currentProposal.id,
				})
			)) as {
				data: { getProposals: ReduxProposals }
			}
			const originalGetProposals = cloneDeep(getProposals)
			delete getProposals._deleted
			delete getProposals.quote
			delete getProposals.invoices

			console.log('getProposals: ', getProposals)
			const { getProposals: updateProposalData, updateContact } =
				createUpdateData({
					getProposals,
					newProposal,
					getContact,
					getCompany,
				})
			console.log('getProposals2: ', getProposals)
			console.log('updateProposalData: ', updateProposalData)

			const proposalData = calculateProposalAndInvoiceTotals({
				getProposals: updateProposalData,
				newProposal,
				getContact,
				quoteTotals: quoteTotals ?? null,
				getCompany,
			})
			console.log('proposalData: ', proposalData)

			getProposals.drafts = proposalData.drafts
			getProposals.noofInvoices = proposalData.invoiceCount
			getProposals.paymentTerms = proposalData.paymentTerms
			if (
				getProposals.noofInvoices &&
				isNaN(Number.parseInt(getProposals.noofInvoices))
			) {
				makeErrorToast(
					'Something Unexpected Happened With Invoice Count!'
				)
			}
			const updateInput = getProperUpdateValues({
				updatedInput: getProposals,
				originalInput: originalGetProposals,
			}) as Record<string, unknown>
			let updatedProposal: Partial<ReduxProposals> & {
				id: string
			}
			if (hasBusinessChanges(updateInput)) {
				const {
					data: { updateProposals: updatedProposalData },
				} = (await API.graphql(
					graphqlOperation(
						customMutations.updateProposalsSimplified,
						{
							input: updateInput,
						}
					)
				)) as {
					data: { updateProposals: ReduxProposals }
				}
				updatedProposal = updatedProposalData
			} else {
				updatedProposal = {
					id: originalGetProposals.id,
				}
			}
			// After simplified update, re-fetch full proposal to restore nested relations (quote, project, etc.)
			try {
				const {
					data: { getProposals: refreshedProposal },
				} = (await API.graphql(
					graphqlOperation(queries.getProposals, {
						id: updatedProposal.id,
					})
				)) as {
					data: { getProposals: ReduxProposals }
				}
				dispatch(setCurrentProposal(refreshedProposal))
			} catch (refetchErr) {
				const errMessage = getErrorMessage(refetchErr)
				return rejectWithValue(errMessage)
			}
			if (Object.keys(updateContact).length > 0) {
				const contactUpdateInput: Record<string, unknown> = {
					...updateContact,
					id: getContact.id,
				}
				await API.graphql(
					graphqlOperation(mutations.updateContact, {
						input: contactUpdateInput,
					})
				)
			}
			if (didBankChange(updateInput)) {
				await propagateProposalBankToInvoices(getProposals)
			}
			await dispatch(fetchProposals())
			await dispatch(fetchContacts())
			const {
				data: {
					invoicesByProposalId: { items },
				},
			} = (await API.graphql(
				graphqlOperation(queries.invoicesByProposalId, {
					proposalId: getProposals.id,
					filter: { activeFlag: { eq: true } },
				})
			)) as {
				data: {
					invoicesByProposalId: { items: ReduxInvoices[] }
				}
			}
			const sortedInvoices = orderBy(items, ['invoiceNumber'], ['asc'])
			await dispatch(setCurrentProposalInvoices(sortedInvoices))
			makeSuccessToast(tProposals('toasts.updatedProposal'))
		} catch (err) {
			makeErrorToast(msg)
			const errorMessage = getErrorMessage(err)
			return rejectWithValue(errorMessage)
		}
	}
)

export const updateProposalAmount = createAsyncThunk<
	void,
	UpdateProposalAmountArgs,
	AsyncThunkConfig
>(
	'proposals/updateProposalAmount',
	async ({ currentProposal }, { dispatch, rejectWithValue }) => {
		const tProposals = i18next.getFixedT(null, 'proposals')
		try {
			const {
				data: { getQuote: originalQuote },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, {
					id: currentProposal.quoteId,
				})
			)) as {
				data: { getQuote: ReduxQuote }
			}
			const quoteInfo = await fetchQuoteInfoUtil(currentProposal.quoteId)
			const recapInfo = await fetchRecapInfoUtil(currentProposal.quoteId)
			dispatch(quoteInfoApi.util.invalidateTags([{ type: 'QuoteInfo' }]))
			dispatch(recapInfoApi.util.invalidateTags([{ type: 'RecapInfo' }]))
			const quoteTotals = (await getQuoteTotalSummary({
				quoteDetails: originalQuote,
				quoteInfo,
				recapInfo,
			})) as QuoteTotals
			await API.graphql(
				graphqlOperation(customMutations.updateProposalsSimplified, {
					input: {
						id: currentProposal.id,
						total: quoteTotals.discount?.totalAfterDiscount,
					},
				})
			)
			dispatch(fetchProposals())
			makeSuccessToast(tProposals('toasts.updatedProposal'))
		} catch (err) {
			makeErrorToast(msg)
			const errorMessage = getErrorMessage(err)
			return rejectWithValue(errorMessage)
		}
	}
)

export const removeProposal = createAsyncThunk<
	void,
	RemoveProposalArgs,
	AsyncThunkConfig
>(
	'clients/removeProposal',
	async (
		{ quoteData, isQuoteList = false },
		{ dispatch, rejectWithValue }
	) => {
		const tProposals = i18next.getFixedT(null, 'proposals')
		try {
			const {
				data: { getQuote: originalQuote2 },
			} = (await API.graphql(
				graphqlOperation(queries.getQuote, { id: quoteData.id })
			)) as {
				data: { getQuote: ReduxQuote }
			}
			const {
				data: {
					proposalsByQuoteId: { items },
				},
			} = (await API.graphql(
				graphqlOperation(queries.proposalsByQuoteId, {
					quoteId: quoteData.id,
					filter: { activeFlag: { eq: true } },
				})
			)) as {
				data: {
					proposalsByQuoteId: { items: ReduxProposals[] }
				}
			}

			console.log('originalQuote2: ', originalQuote2)
			if (originalQuote2?.proposals?.items.length === 0) {
				makeWarningToast(tProposals('toasts.proposalDoesntExists'))
				return
			}
			if (
				originalQuote2?.proposals?.items[0]?.drafts &&
				originalQuote2?.proposals?.items[0]?.drafts.length > 0 &&
				originalQuote2?.proposals?.items[0]?.drafts.some(
					(d) => !!d?.generated
				)
			) {
				makeWarningToast(tProposals('toasts.invGenerated'))
				return
			}
			originalQuote2.isChecked = false
			delete originalQuote2.proposals
			delete originalQuote2._deleted

			const quoteUpdateInput: Record<string, unknown> = {
				id: quoteData.id,
				isChecked: false,
			}

			await API.graphql(
				graphqlOperation(mutations.updateQuote, {
					input: quoteUpdateInput,
				})
			)

			const updateObj: Record<string, unknown> = {
				id: items[0]?.id,
				activeFlag: false,
				quoteId: 'deleted',
			}
			await API.graphql(
				graphqlOperation(customMutations.updateProposalsSimplified, {
					input: updateObj,
				})
			)
			// Invalidate projectChildren cache so RTK Query refetches
			await dispatch(
				projectApi.util.invalidateTags([
					{
						type: 'Projects',
						id: `${quoteData.parentProjectId}-children`,
					},
				])
			)
			if (isQuoteList) {
				dispatch(fetchAllQuotes({}))
			}
			makeSuccessToast(tProposals('toasts.removedProposal'))
		} catch (err) {
			console.log('err in remove proposal: ', err)
			makeErrorToast(msg)
			const errorMessage = getErrorMessage(err)
			return rejectWithValue(errorMessage)
		}
	}
)
export const toggleExpiryStatus = createAsyncThunk<
	void,
	ToggleExpiryArgs,
	AsyncThunkConfig
>(
	'clients/toggleExpiryStatus',
	async ({ proposalId }, { dispatch, rejectWithValue }) => {
		const tProposals = i18next.getFixedT(null, 'proposals')
		try {
			const {
				data: { getProposals },
			} = (await API.graphql(
				graphqlOperation(queries.getProposals, { id: proposalId })
			)) as {
				data: { getProposals: ReduxProposals }
			}
			if (getProposals) {
				const proposalDrafts = getProposals?.drafts
				let newStatus = 'Expired'

				// If proposal is already in expired status toggle it back
				if (getProposals?.status === 'Expired') {
					if (!proposalDrafts) {
						if (
							getProposals?.poDocuments &&
							getProposals?.poDocuments.length > 0
						) {
							newStatus = 'Generate'
						} else {
							newStatus = 'Generate without PO'
						}
					} else {
						if (proposalDrafts.every((p) => p?.generated)) {
							newStatus = 'Generated'
						} else if (proposalDrafts.some((p) => p?.generated)) {
							newStatus = 'Partially Generated'
						} else {
							if (
								getProposals?.poDocuments &&
								getProposals?.poDocuments.length > 0
							) {
								newStatus = 'Generate'
							} else {
								newStatus = 'Generate without PO'
							}
						}
					}
				}

				const updateProposalObject: Record<string, unknown> = {
					id: getProposals?.id,
					status: newStatus,
				}
				console.log('updateProposalObject: ', updateProposalObject)
				await API.graphql(
					graphqlOperation(
						customMutations.updateProposalsSimplified,
						{
							input: updateProposalObject,
						}
					)
				)
				dispatch(fetchProposals())
				makeSuccessToast(tProposals('toasts.success'))
			}
		} catch (err) {
			makeErrorToast(msg)
			const errorMessage = getErrorMessage(err)
			return rejectWithValue(errorMessage)
		}
	}
)

export const proposalsSlice = createSlice({
	name: 'proposals',
	initialState,
	reducers: {
		setProposalsPOModalOpenStatus: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.proposalsPOModalOpenStatus = action.payload
		},
		setEditProposalsPOModalOpenStatus: (
			state,
			action: PayloadAction<boolean>
		) => {
			state.editProposalsPOModalOpenStatus = action.payload
		},
		setFetchProposalsApiStatus: (state) => {
			state.fetchProposalsApiStatus = 'loading'
		},
		setProposalModalOpenStatus: (
			state,
			action: PayloadAction<{ status: boolean }>
		) => {
			state.proposalModalOpenStatus = action.payload.status
		},
		setPdfModalStatusOpen: (
			state,
			action: PayloadAction<{ status: boolean }>
		) => {
			state.pdfModalStatusOpen = action.payload.status
		},
		setCurrentProposal: (
			state,
			action: PayloadAction<ReduxProposals | null>
		) => {
			state.currentProposal = action.payload
		},
		setCurrentProposalInvoices: (
			state,
			action: PayloadAction<ReduxInvoices[] | null>
		) => {
			state.currentProposalInvoices = action.payload
		},
		setCurrentDraftIndex: (state, action: PayloadAction<number | null>) => {
			state.currentDraftIndex = action.payload
		},
		setCurrentDraftInvoiceId: (
			state,
			action: PayloadAction<string | null>
		) => {
			state.currentDraftInvoiceId = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchProposals.pending, (state) => {
			state.fetchProposalsApiStatus = 'loading'
		})
		builder.addCase(fetchProposals.fulfilled, (state, action) => {
			state.fetchProposalsApiStatus = 'succeeded'
			state.proposalsData = action.payload
		})
		builder.addCase(fetchProposals.rejected, (state, action) => {
			state.fetchProposalsApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(createProposal.pending, (state) => {
			state.createProposalApiStatus = 'loading'
		})
		builder.addCase(createProposal.fulfilled, (state) => {
			state.createProposalApiStatus = 'succeeded'
			state.proposalModalOpenStatus = false
		})
		builder.addCase(createProposal.rejected, (state, action) => {
			state.createProposalApiStatus = 'failed'
			state.proposalModalOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(addPOToProposal.pending, (state) => {
			state.addPOToProposalApiStatus = 'loading'
		})
		builder.addCase(addPOToProposal.fulfilled, (state) => {
			state.addPOToProposalApiStatus = 'succeeded'
			state.proposalsPOModalOpenStatus = false
		})
		builder.addCase(addPOToProposal.rejected, (state, action) => {
			state.addPOToProposalApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(editPOInProposal.pending, (state) => {
			state.editPOInProposalApiStatus = 'loading'
		})
		builder.addCase(editPOInProposal.fulfilled, (state) => {
			state.editPOInProposalApiStatus = 'succeeded'
			state.editProposalsPOModalOpenStatus = false
		})
		builder.addCase(editPOInProposal.rejected, (state, action) => {
			state.editPOInProposalApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(updateProposalProgress.pending, (state) => {
			state.updateProposalApiStatus = 'loading'
		})
		builder.addCase(updateProposalProgress.fulfilled, (state) => {
			state.updateProposalApiStatus = 'succeeded'
			state.proposalModalOpenStatus = false
		})
		builder.addCase(updateProposalProgress.rejected, (state, action) => {
			state.updateProposalApiStatus = 'failed'
			state.proposalModalOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(updateProposal.pending, (state) => {
			state.updateProposalApiStatus = 'loading'
		})
		builder.addCase(updateProposal.fulfilled, (state) => {
			state.updateProposalApiStatus = 'succeeded'
			state.proposalModalOpenStatus = false
		})
		builder.addCase(updateProposal.rejected, (state, action) => {
			state.updateProposalApiStatus = 'failed'
			state.proposalModalOpenStatus = false
			state.error = action.error.message ?? null
		})
		builder.addCase(updateProposalFromPreview.pending, (state) => {
			state.updateProposalFromPreviewApiStatus = 'loading'
		})
		builder.addCase(updateProposalFromPreview.fulfilled, (state) => {
			state.updateProposalFromPreviewApiStatus = 'succeeded'
			state.pdfModalStatusOpen = true
		})
		builder.addCase(updateProposalFromPreview.rejected, (state, action) => {
			state.updateProposalFromPreviewApiStatus = 'failed'
			state.pdfModalStatusOpen = false
			state.error = action.error.message ?? null
		})
		builder.addCase(updateProposalAmount.pending, (state) => {
			state.updateProposalAmountApiStatus = 'loading'
		})
		builder.addCase(updateProposalAmount.fulfilled, (state) => {
			state.updateProposalAmountApiStatus = 'succeeded'
		})
		builder.addCase(updateProposalAmount.rejected, (state, action) => {
			state.updateProposalAmountApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(removeProposal.pending, (state) => {
			state.removeProposalApiStatus = 'loading'
		})
		builder.addCase(removeProposal.fulfilled, (state) => {
			state.removeProposalApiStatus = 'succeeded'
		})
		builder.addCase(removeProposal.rejected, (state, action) => {
			state.removeProposalApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
		builder.addCase(toggleExpiryStatus.pending, (state) => {
			state.toggleExpiryApiStatus = 'loading'
		})
		builder.addCase(toggleExpiryStatus.fulfilled, (state) => {
			state.toggleExpiryApiStatus = 'succeeded'
		})
		builder.addCase(toggleExpiryStatus.rejected, (state, action) => {
			state.toggleExpiryApiStatus = 'failed'
			state.error = action.error.message ?? null
		})
	},
})

export const {
	setProposalsPOModalOpenStatus,
	setEditProposalsPOModalOpenStatus,
	setProposalModalOpenStatus,
	setCurrentProposal,
	setCurrentProposalInvoices,
	setFetchProposalsApiStatus,
	setPdfModalStatusOpen,
	setCurrentDraftIndex,
	setCurrentDraftInvoiceId,
} = proposalsSlice.actions

export default proposalsSlice.reducer
